import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../services/api';
import PageHeader from '../components/PageHeader';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { BarChart3, Clock, Shield, Briefcase } from 'lucide-react';

const REPORT_TYPES = [
  { id: 'billing', label: 'Billing & Revenue', icon: BarChart3 },
  { id: 'time', label: 'Time & Productivity', icon: Clock },
  { id: 'trust', label: 'Trust Accounts', icon: Shield },
  { id: 'cases', label: 'Case Analytics', icon: Briefcase },
];

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

export default function Reports() {
  const [reportType, setReportType] = useState('billing');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { type: reportType };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const { data: d } = await reportsAPI.get(params);
      setData(d);
    } catch (err) {
      setError('Could not load report. Please ensure the server is running.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, [reportType]);

  return (
    <div className="p-6">
      <PageHeader title="Reports & Analytics" subtitle="Insights into your firm's performance" />

      {/* Report Type Selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {REPORT_TYPES.map(r => (
          <button
            key={r.id}
            onClick={() => setReportType(r.id)}
            className={`card p-4 text-left transition-all flex items-center gap-3 ${reportType === r.id ? 'border-2 border-blue-500 shadow-md' : 'hover:shadow-md'}`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${reportType === r.id ? 'bg-blue-600' : 'bg-gray-100'}`}>
              <r.icon size={18} className={reportType === r.id ? 'text-white' : 'text-gray-500'} />
            </div>
            <span className={`text-sm font-medium ${reportType === r.id ? 'text-blue-700' : 'text-gray-700'}`}>{r.label}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="card mb-5 p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="label">From</label>
          <input type="date" className="input w-40" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="label">To</label>
          <input type="date" className="input w-40" value={dateTo} onChange={e => setDateTo(e.target.value)} />
        </div>
        <button className="btn-primary" onClick={loadReport}>Generate Report</button>
      </div>

      {error && (
        <div className="card p-6 text-center text-red-600 mb-4">
          <p className="font-medium">{error}</p>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : !data ? null : (
        <div className="space-y-5">
          {/* Billing Report */}
          {reportType === 'billing' && (
            <>
              <div className="card">
                <p className="text-3xl font-bold text-green-700">${parseFloat(data.total_collected || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                <p className="text-sm text-gray-500 mt-1">Total Revenue Collected</p>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card">
                  <h3 className="font-semibold text-gray-900 mb-4">Revenue by Payment Method</h3>
                  {data.by_method?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={data.by_method} dataKey="total" nameKey="payment_method" cx="50%" cy="50%" outerRadius={90} label={({ payment_method, percent }) => `${payment_method} ${(percent*100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                          {data.by_method.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => `$${parseFloat(v).toFixed(2)}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-12">No data</p>}
                </div>
                <div className="card">
                  <h3 className="font-semibold text-gray-900 mb-4">Revenue by Month</h3>
                  {data.by_month?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={data.by_month}>
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `R${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={v => `$${parseFloat(v).toFixed(2)}`} />
                        <Bar dataKey="total" fill="#10b981" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-12">No data</p>}
                </div>
              </div>
            </>
          )}

          {/* Time Report */}
          {reportType === 'time' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="card text-center">
                  <div className="text-3xl font-bold text-blue-700">{parseFloat(data.total_hours || 0).toFixed(1)}h</div>
                  <div className="text-sm text-gray-500 mt-1">Total Hours</div>
                </div>
                <div className="card text-center">
                  <div className="text-3xl font-bold text-amber-600">{parseFloat(data.unbilled_hours || 0).toFixed(1)}h</div>
                  <div className="text-sm text-gray-500 mt-1">Unbilled Hours</div>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card">
                  <h3 className="font-semibold text-gray-900 mb-4">Hours by Staff</h3>
                  {data.by_employee?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={data.by_employee.map(e => ({ name: `${e.employee__first_name} ${e.employee__last_name}`.trim(), hours: parseFloat(e.hours || 0) }))}>
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="hours" fill="#3b82f6" radius={[4,4,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <p className="text-gray-400 text-center py-12">No data</p>}
                </div>
                <div className="card">
                  <h3 className="font-semibold text-gray-900 mb-4">Top Cases by Hours</h3>
                  {data.by_case?.length > 0 ? (
                    <div className="space-y-2">
                      {data.by_case.map((c, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate text-gray-700">{c.case__case_title || 'Unknown'}</p>
                            <div className="mt-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-2 bg-blue-500 rounded-full" style={{ width: `${(parseFloat(c.hours) / parseFloat(data.by_case[0].hours)) * 100}%` }} />
                            </div>
                          </div>
                          <span className="text-sm font-medium text-gray-700 flex-shrink-0">{parseFloat(c.hours).toFixed(1)}h</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-gray-400 text-center py-12">No data</p>}
                </div>
              </div>
            </>
          )}

          {/* Trust Report */}
          {reportType === 'trust' && (
            <>
              <div className="card">
                <p className="text-3xl font-bold text-purple-700">${parseFloat(data.total_balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                <p className="text-sm text-gray-500 mt-1">Total Trust Funds Under Management</p>
              </div>
              <div className="card p-0 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b"><tr>{['Client', 'Bank', 'Account #', 'Balance'].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>)}</tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.accounts?.map(a => (
                      <tr key={a.id}><td className="px-4 py-3">{a.client_name}</td><td className="px-4 py-3">{a.bank_name}</td><td className="px-4 py-3">{a.account_number}</td><td className="px-4 py-3 font-bold text-purple-700">${parseFloat(a.current_balance).toFixed(2)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Cases Report */}
          {reportType === 'cases' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Cases by Status</h3>
                {data.by_status?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={data.by_status} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={75} label>
                        {data.by_status.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-gray-400 text-center py-8">No data</p>}
              </div>
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Cases by Type</h3>
                {data.by_type?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.by_type} layout="vertical">
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="case_type" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#6366f1" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="text-gray-400 text-center py-8">No data</p>}
              </div>
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Cases by Attorney</h3>
                {data.by_attorney?.length > 0 ? (
                  <div className="space-y-2">
                    {data.by_attorney.map((a, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{`${a.assigned_attorney__first_name || 'Unassigned'} ${a.assigned_attorney__last_name || ''}`.trim()}</span>
                        <span className="text-sm font-bold text-gray-900">{a.count}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-gray-400 text-center py-8">No data</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
